import {
  createUseInvokeClient,
  IndieTabletopClient,
  redeemedPledge,
} from "@indietabletop/appkit";
import { featureUnlock } from "@indietabletop/types";
import {
  array,
  assign,
  boolean,
  enums,
  literal,
  nullable,
  number,
  object,
  optional,
  partial,
  string,
  union,
  unknown,
  type Infer,
} from "superstruct";
import { gameDataGroups, warbandSnapshotData } from "../types/lib";
import { config } from "./config";

export type ProductOwnership = Infer<ReturnType<typeof productOwnership>>;

function productOwnership() {
  return object({
    grantedTs: number(),
    downloadUrl: optional(string()),
    downloadUrlExpiresTs: optional(number()),
    downloadFileSize: optional(string()),
    downloadFileVersion: optional(string()),
  });
}

export type FileDemo = Infer<ReturnType<typeof fileDemo>>;

function fileDemo() {
  return object({ type: literal("FILE"), src: string() });
}

export type ProductType = Infer<ReturnType<typeof productType>>;

function productType() {
  return enums(["DIGITAL", "DIGITAL_BOOK", "DIGITAL_MODEL"]);
}

export type Product = Infer<ReturnType<typeof product>>;

function product() {
  return object({
    id: string(),
    name: string(),
    description: string(),
    price: number(),
    type: productType(),
    isEarlyAccess: boolean(),
    currency: string(),
    code: string(),
    thumbnailSrc: optional(nullable(string())),
    previewSrcs: array(string()),
    unlocks: optional(array(featureUnlock())),
    demos: array(union([fileDemo(), object({ type: literal("IN_APP") })])),

    ownership: nullable(productOwnership()),
    updatedTs: nullable(number()),
    createdTs: number(),
  });
}

class GreathelmClient<S, G, R> extends IndieTabletopClient<S, G, R> {
  async getGreathelmUnlocks() {
    return await this.getUnlocks("greathelm");
  }

  // TODO: Add to AppKit
  override async redeemPledge(campaignCode: string, id: string) {
    return await this.fetch(
      `/v1/redemptions/${campaignCode}/pledges/${id}`,
      assign(
        redeemedPledge(),
        object({ thumbnailSrc: optional(nullable(string())) }),
      ),
    );
  }

  // TODO: Add to AppKit
  async getShopProducts(appCode: string, currency: string) {
    const params = new URLSearchParams({ currency });
    return await this.fetch(
      `/v1/apps/${appCode}/products?${params}`,
      object({
        items: array(product()),
      }),
    );
  }
}

export const client = new GreathelmClient({
  apiOrigin: config.VITE_ITC_API_ORIGIN,
  validation: {
    snapshot: warbandSnapshotData(),
    gameData: partial({ greathelm: gameDataGroups() }),
    ruleset: unknown(),
  },
});

export const useInvokeClient = createUseInvokeClient(client);
